// What an analytics event may carry, and nothing else. No Firebase in here, so these rules are checked in
// plain Node (`npm run test:js`) and every event goes through them on its way out.
//
// Three things are never sent (phase 1.17): message bodies, secrets (key plaintext, webhook secrets,
// tokens), and people (emails, names, anything typed into a field). The call sites already avoid them;
// `sanitize` is the backstop for the call site that forgets.

const MAX_KEY_LENGTH = 40
const MAX_VALUE_LENGTH = 100

// This service's ids (IdPrefix): a two-letter prefix, an underscore and a random tail. One record must
// never become one GA4 value, so an id in a path or a chosen value collapses to "id".
const ID_PREFIXES = ["ak", "ap", "cp", "cs", "ct", "cv", "ev", "jc", "ms", "og", "op", "sp", "wa", "wd", "wh"]
const PREFIXED_ID = new RegExp(`^(${ID_PREFIXES.join("|")})_[a-z0-9]+$`, "i")
// A webhook test send's event id (Webhooks::Test), which has no events row.
const TEST_EVENT_ID = /^evt_test_[a-z0-9]+$/i
const NUMBER = /^\d+$/

// Console routes whose next path segment is a secret: an invitation, an email verification, a password
// reset. Firebase records the page on every event, so the token must not survive into it.
const TOKEN_ROUTES = new Set(["invitations", "verify_email", "reset_password"])

// A param whose name says it holds one of the never-sent things. Matched on whole snake_case words, so
// `field_type` and `context` pass and `webhook_url`, `email`, `key_plaintext` and `body_text` do not.
const SENSITIVE_WORDS = new Set(["secret", "token", "password", "email", "body", "text", "plaintext", "url", "phone"])

export const isId = (value) => {
  const text = String(value)
  return PREFIXED_ID.test(text) || TEST_EVENT_ID.test(text) || NUMBER.test(text)
}

// A label, a field name or a title, as a stable snake_case key. Anything that is not a plain string or
// number (a React node) falls back rather than serialising React internals into an event.
export const toEventKey = (value, fallback = "unknown") => {
  if (typeof value !== "string" && typeof value !== "number") return fallback

  const key = String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, MAX_KEY_LENGTH)

  return key || fallback
}

// What a control chose, unless the choice is a record's id: "failed" is the answer, "wh_…" is one GA4
// value per endpoint. Collapsed to "id" rather than dropped, so "picked an endpoint" still reads
// differently from "cleared the filter".
export const toEventValue = (value) => {
  if (value == null || value === "") return null
  if (typeof value === "boolean") return value

  const text = String(value)
  return isId(text) ? "id" : text.slice(0, MAX_VALUE_LENGTH)
}

// A console URL as a route pattern: no query string, no fragment, ids as ":id" and tokens as ":token".
// "/admin/apps/ap_1x/deliveries?status=failed" reports as "/admin/apps/:id/deliveries".
export const collapsePath = (url) => {
  if (typeof url !== "string") return null

  const segments = url.split(/[?#]/)[0].split("/")
  return segments
    .map((segment, index) => {
      if (!segment) return segment
      if (TOKEN_ROUTES.has(segments[index - 1])) return ":token"
      return isId(segment) ? ":id" : segment
    })
    .join("/")
    .slice(0, MAX_VALUE_LENGTH)
}

const sensitive = (key) => key.toLowerCase().split("_").some((word) => SENSITIVE_WORDS.has(word))

const sendable = (value) => value === null || ["string", "number", "boolean"].includes(typeof value)

// The params an event actually sends: plain values only (GA4 takes nothing else), never a sensitive
// name, and strings capped at GA4's 100-character limit.
export const sanitize = (params) => {
  if (params == null || typeof params !== "object") return {}

  return Object.fromEntries(
    Object.entries(params)
      .filter(([key, value]) => !sensitive(key) && sendable(value))
      .map(([key, value]) => [key, typeof value === "string" ? value.slice(0, MAX_VALUE_LENGTH) : value]),
  )
}

// A button's visible label as plain text, for its event name. Only the string parts count: a count in a
// label ("Retry {n} selected") would make one event name per number.
export const labelText = (children) => {
  if (typeof children === "string") return children
  if (Array.isArray(children)) return children.filter((part) => typeof part === "string").join(" ").trim() || null
  return null
}
